import React, { useState, useRef, useEffect, useCallback, KeyboardEvent, ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  InputContainer,
  InputWrapper,
  TextAreaContainer,
  TextArea,
  InputActions,
  SendButton,
  SendButtonDisabled,
  MicButton,
  MicButtonActive,
  InputHint,
} from './ChatBox.styles';
import { ActionButton } from "@react-spectrum/s2";
import { AudioWave as AudioWaveIcon, Send as SendIcon } from '../icons/s2';

const ADOBE_GEN_AI_GUIDELINES_URL =
  'https://www.adobe.com/legal/licenses-terms/adobe-gen-ai-user-guidelines.html';

/** Min / max height for multi-line composer (px) — ~3 lines min, grows with content */
const TEXTAREA_MIN_HEIGHT_PX = 72;
const TEXTAREA_MAX_HEIGHT_PX = 220;

interface MessageInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  /** Shown in placeholder: "Ask me anything related to …" */
  siteDisplayName?: string;
  /** When `key` changes, replace composer text and focus (e.g. suggested question chip). */
  composerPrefill?: { key: number; text: string } | null;
  onComposerPrefillApplied?: () => void;
}

const isSpeechRecognitionSupported = (): boolean =>
  typeof window !== 'undefined' &&
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled,
  siteDisplayName,
  composerPrefill,
  onComposerPrefillApplied,
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const inputBeforeListeningRef = useRef('');

  useEffect(() => {
    const el = textAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(
      Math.max(el.scrollHeight, TEXTAREA_MIN_HEIGHT_PX),
      TEXTAREA_MAX_HEIGHT_PX,
    );
    el.style.height = `${next}px`;
  }, [input]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!composerPrefill?.text?.trim()) return;
    setInput(composerPrefill.text);
    onComposerPrefillApplied?.();
    queueMicrotask(() => {
      const el = textAreaRef.current;
      if (!el) return;
      el.focus();
      el.style.height = 'auto';
      const next = Math.min(
        Math.max(el.scrollHeight, TEXTAREA_MIN_HEIGHT_PX),
        TEXTAREA_MAX_HEIGHT_PX,
      );
      el.style.height = `${next}px`;
    });
    // Apply once per prefill `key`; omit callback from deps to avoid identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onComposerPrefillApplied is a stable clear handler
  }, [composerPrefill?.key, composerPrefill?.text]);

  const handleSend = () => {
    const trimmedInput = input.trim();
    if (trimmedInput && !disabled) {
      if (isListening) {
        recognitionRef.current?.stop();
        setIsListening(false);
      }
      onSend(trimmedInput);
      setInput('');
      if (textAreaRef.current) {
        const el = textAreaRef.current;
        el.style.height = 'auto';
        el.style.height = `${TEXTAREA_MIN_HEIGHT_PX}px`;
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const toggleListening = useCallback(() => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;

    inputBeforeListeningRef.current = input;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = Array.from({ length: event.results.length })
        .map((_, i) => event.results[i][0].transcript)
        .join('');

      const prefix = inputBeforeListeningRef.current;
      const separator = prefix && !prefix.endsWith(' ') ? ' ' : '';
      setInput(prefix + separator + transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
      textAreaRef.current?.focus();
    };
    recognition.onerror = () => {
      setIsListening(false);
      textAreaRef.current?.focus();
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, input]);

  const isSendDisabled = disabled || !input.trim();
  const voiceSupported = isSpeechRecognitionSupported();

  const domainName = siteDisplayName ?? t('chat.input.defaultDomain');
  const placeholder = isListening
    ? t('chat.input.listening')
    : t('chat.input.placeholder', { siteDisplayName: domainName });

  const fieldCore = (
    <div className={`${TextAreaContainer} textarea-container--gradient-inner`}>
      <textarea
        ref={textAreaRef}
        id="chat-message-input"
        name="chat-message"
        className={TextArea}
        placeholder={placeholder}
        value={input}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        rows={3}
      />
      <div className={InputActions}>
        {/* Spectrum ActionButton drops `title`, so the unsupported hint rides on a wrapper. */}
        <span
          className="input-actions__mic-wrap"
          title={!voiceSupported ? t('chat.input.voiceUnsupported') : undefined}
        >
          <ActionButton
            isQuiet
            UNSAFE_className={`${MicButton} ${isListening ? MicButtonActive : ''}`}
            onPress={voiceSupported ? toggleListening : undefined}
            isDisabled={disabled || !voiceSupported}
            aria-label={
              !voiceSupported
                ? t('chat.input.voiceUnsupported')
                : isListening
                  ? t('chat.input.stopListening')
                  : t('chat.input.startVoice')
            }
          >
            <AudioWaveIcon />
          </ActionButton>
        </span>
        <ActionButton
          isQuiet
          UNSAFE_className={`${SendButton} ${isSendDisabled ? SendButtonDisabled : ''}`}
          onPress={handleSend}
          isDisabled={isSendDisabled}
          aria-label={t('chat.input.send')}
        >
          <SendIcon />
        </ActionButton>
      </div>
    </div>
  );

  return (
    <div className={`${InputContainer} input-container--hero`}>
      <div className={InputWrapper}>
        <div className="chat-input-gradient-ring">{fieldCore}</div>
        <p className={`${InputHint} input-hint--hero`}>
          <Trans
            i18nKey="chat.input.disclaimer"
            components={{
              guidelines: (
                <a
                  href={ADOBE_GEN_AI_GUIDELINES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="input-hint--hero__link"
                >
                  Generative AI User Guidelines
                </a>
              ),
            }}
          />
        </p>
      </div>
    </div>
  );
};

export default MessageInput;
